import React, { useId, useState } from 'react';

import { cn } from '../helpers/cn';
import { CustomSVGProps } from '../icons/CustomSVGProps.types';
import { IconCheckedSolid24 } from '../icons/ic-checkedSolid';

export type CustomCheckboxIcons = {
    checked: React.JSX.Element;
    unchecked: React.JSX.Element;
};

export enum CheckboxVariant {
    /** On a light/white background: dark-blue check, gray unchecked box. */
    default = 'default',
    /** On a colored/dark surface: white check, translucent-white unchecked box. */
    onSurface = 'onSurface'
}

export type CheckboxProps = {
    checked?: boolean;
    defaultChecked?: boolean;
    disabled?: boolean;
    onChange?: (state: boolean) => void;
    className?: string;
    iconClassName?: string;
    iconProps?: { checked?: CustomSVGProps; unchecked?: CustomSVGProps };
    size?: 'large' | 'medium' | 'small';
    variant?: CheckboxVariant;
    /** Associates an external `<label htmlFor>` with the underlying native input. */
    id?: string;
    /** Accessible name for the checkbox when no visible label is associated. */
    ariaLabel?: string;
    /** Id of an element that describes the checkbox, e.g. a helper sentence beside it. */
    ariaDescribedBy?: string;
    /** @deprecated Will be removed. Use the `checked` and `defaultChecked` properties. */
    controlled?: boolean;
    /** @deprecated Will be removed. Use `variant={CheckboxVariant.onSurface}` instead. */
    isLight?: boolean;
    customIcons?: CustomCheckboxIcons;
};

function Checkbox({
    checked: controlledChecked,
    defaultChecked = false,
    disabled = false,
    onChange,
    className,
    iconClassName,
    iconProps,
    size = 'medium',
    variant = CheckboxVariant.default,
    id,
    ariaLabel,
    ariaDescribedBy,
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- backward-compat bridge to `variant`
    isLight = false,
    customIcons
}: CheckboxProps): React.JSX.Element {
    // `isLight` is kept for backward compatibility and maps onto the onSurface variant.
    const isOnSurface = variant === CheckboxVariant.onSurface || isLight;
    const isControlled = controlledChecked !== undefined;
    const [internalChecked, setInternalChecked] = useState(defaultChecked);

    const generatedId = useId();
    const inputId = id ?? generatedId;

    const isChecked = isControlled ? controlledChecked : internalChecked;

    const handleChange = (newChecked: boolean) => {
        if (disabled) return;

        if (!isControlled) {
            setInternalChecked(newChecked);
        }

        if (onChange) {
            onChange(newChecked);
        }
    };

    const iconClass = {
        large: 'w-32 h-32',
        medium: 'w-24 h-24',
        small: 'w-16 h-16'
    }[size];

    const checkedIcon = customIcons?.checked ?? (
        <IconCheckedSolid24
            className={cn(
                iconClass,
                isOnSurface ? 'text-[var(--primary-middle-color)]' : 'text-[var(--primary-color)]',
                iconClassName
            )}
            foregroundcolor="var(--on-primary-color)"
            {...iconProps?.checked}
        />
    );
    const uncheckedIcon = customIcons?.unchecked ?? (
        // The default unchecked box carries a subtle dark border on top of its translucent fill:
        // the white/20 fill alone is invisible on light surfaces (e.g. the white multi-select
        // dropdown lists), while many long-standing usages sit on dark panels and rely on that
        // fill — the border makes the box legible on both without changing either variant's look.
        <div
            className={cn(
                iconClass,
                'rounded-2',
                isOnSurface ? 'bg-darkBlue/10' : 'bg-white/20 border-1 border-darkBlue/25'
            )}
        />
    );

    return (
        <span
            className={cn(className, 'relative inline-flex', {
                'cursor-pointer': !disabled,
                'cursor-not-allowed': disabled
            })}>
            <input
                type="checkbox"
                id={inputId}
                className="peer absolute inset-0 z-[1] m-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                checked={isChecked}
                disabled={disabled}
                aria-label={ariaLabel}
                aria-describedby={ariaDescribedBy}
                onChange={(event) => handleChange(event.target.checked)}
            />
            <span className="relative inline-flex rounded-2 peer-focus-visible:ring-2 peer-focus-visible:ring-[rgba(var(--primary-color-rgb),0.6)] peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-transparent">
                <span
                    className={cn('absolute top-0 left-0 flex-none transition-opacity duration-100', {
                        'opacity-0': isChecked
                    })}>
                    {uncheckedIcon}
                </span>
                <span
                    className={cn('flex-none transition-opacity duration-100', {
                        'opacity-0': !isChecked
                    })}>
                    {checkedIcon}
                </span>
            </span>
        </span>
    );
}

export default Checkbox;
